import * as THREE from 'three';

// Seamless, tileable facade texture set for the OSM city, drawn on canvases so
// there's zero asset dependency. Three maps:
//   makeFacade() — concrete wall + a 4×4 window grid (the daytime surface)
//   makeGlow()   — black with a random subset of windows lit (night emissive)
//   makeRoof()   — dark gravel rooftop
// All tile seamlessly (windows are inset in each cell, so the seam lands on
// wall). Applied via triplanar projection in OsmCity, so no UVs are needed.

function toTexture(canvas) {
  const t = new THREE.CanvasTexture(canvas);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 8;
  return t;
}

// Deterministic PRNG so window-lighting is stable across reloads.
function mulberry32(seed) {
  return function () {
    seed |= 0; seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const GRID = 4; // windows per tile edge

export function makeFacade() {
  const S = 512;
  const c = document.createElement('canvas'); c.width = c.height = S;
  const x = c.getContext('2d');
  // Concrete base + faint vertical streaking for weathering.
  x.fillStyle = '#b4b0a8'; x.fillRect(0, 0, S, S);
  for (let i = 0; i < 200; i++) {
    const gx = Math.random() * S;
    x.fillStyle = `rgba(${(110 + Math.random() * 40) | 0},${(106 + Math.random() * 40) | 0},${(100 + Math.random() * 40) | 0},0.05)`;
    x.fillRect(gx, 0, 1 + Math.random() * 2, S);
  }
  const cw = S / GRID, ch = S / GRID, wpad = cw * 0.22, hpad = ch * 0.24;
  for (let r = 0; r < GRID; r++) for (let cI = 0; cI < GRID; cI++) {
    const wx = cI * cw + wpad, wy = r * ch + hpad, ww = cw - wpad * 2, wh = ch - hpad * 2;
    x.fillStyle = '#8a857c'; x.fillRect(wx - 2, wy - 2, ww + 4, wh + 4);            // frame
    const g = x.createLinearGradient(wx, wy, wx, wy + wh);
    g.addColorStop(0, '#3b4650'); g.addColorStop(1, '#232b34');                      // glass
    x.fillStyle = g; x.fillRect(wx, wy, ww, wh);
    x.fillStyle = 'rgba(255,255,255,0.06)'; x.fillRect(wx, wy, ww, wh * 0.28);        // sky reflection
    x.fillStyle = '#9a958b'; x.fillRect(wx - 2, wy + wh, ww + 4, 3);                  // sill
  }
  return toTexture(c);
}

export function makeGlow(seed = 7) {
  const S = 512;
  const c = document.createElement('canvas'); c.width = c.height = S;
  const x = c.getContext('2d');
  x.fillStyle = '#000'; x.fillRect(0, 0, S, S);
  const cw = S / GRID, ch = S / GRID, wpad = cw * 0.22, hpad = ch * 0.24;
  const rand = mulberry32(seed);
  for (let r = 0; r < GRID; r++) for (let cI = 0; cI < GRID; cI++) {
    if (rand() < 0.5) {
      const wx = cI * cw + wpad, wy = r * ch + hpad, ww = cw - wpad * 2, wh = ch - hpad * 2;
      x.fillStyle = rand() < 0.82 ? '#ffcf8a' : '#bfe0ff'; // mostly warm, a few cool
      x.fillRect(wx, wy, ww, wh);
    }
  }
  return toTexture(c);
}

export function makeRoof() {
  const S = 256;
  const c = document.createElement('canvas'); c.width = c.height = S;
  const x = c.getContext('2d');
  x.fillStyle = '#2a2a30'; x.fillRect(0, 0, S, S);
  for (let i = 0; i < 2600; i++) {
    const v = (26 + Math.random() * 30) | 0;
    x.fillStyle = `rgba(${v},${v},${v + 4},0.4)`;
    x.fillRect(Math.random() * S, Math.random() * S, 1, 1);
  }
  return toTexture(c);
}

// Gritty urban asphalt — dark, grainy aggregate, tar-patch repairs, cracks,
// oil stains and grime. Tuned to read as a real street, especially with a
// little wet sheen (low roughness + the reflection environment).
export function makeAsphalt() {
  const S = 512;
  const c = document.createElement('canvas'); c.width = c.height = S;
  const x = c.getContext('2d');
  x.fillStyle = '#1b1b1f'; x.fillRect(0, 0, S, S);
  // aggregate grain
  for (let i = 0; i < 26000; i++) {
    const v = (18 + Math.random() * 46) | 0;
    x.fillStyle = `rgba(${v},${v},${v + 3},${0.25 + Math.random() * 0.4})`;
    x.fillRect(Math.random() * S, Math.random() * S, 1, 1);
  }
  // tar-patch repairs (darker irregular blobs)
  for (let i = 0; i < 8; i++) {
    x.fillStyle = `rgba(8,8,10,${0.3 + Math.random() * 0.3})`;
    x.beginPath();
    const px = Math.random() * S, py = Math.random() * S, rr = 20 + Math.random() * 60;
    for (let a = 0; a < 10; a++) {
      const ang = (a / 10) * Math.PI * 2, rad = rr * (0.6 + Math.random() * 0.6);
      const mx = px + Math.cos(ang) * rad, my = py + Math.sin(ang) * rad;
      a ? x.lineTo(mx, my) : x.moveTo(mx, my);
    }
    x.closePath(); x.fill();
  }
  // cracks
  x.strokeStyle = 'rgba(0,0,0,0.55)'; x.lineWidth = 1;
  for (let i = 0; i < 22; i++) {
    x.beginPath();
    let cx = Math.random() * S, cy = Math.random() * S;
    x.moveTo(cx, cy);
    for (let s = 0; s < 6; s++) { cx += (Math.random() - 0.5) * 60; cy += (Math.random() - 0.5) * 60; x.lineTo(cx, cy); }
    x.stroke();
  }
  // faint oil stains
  for (let i = 0; i < 5; i++) {
    const px = Math.random() * S, py = Math.random() * S, rr = 14 + Math.random() * 30;
    const g = x.createRadialGradient(px, py, 0, px, py, rr);
    g.addColorStop(0, 'rgba(10,10,16,0.5)'); g.addColorStop(1, 'rgba(10,10,16,0)');
    x.fillStyle = g; x.beginPath(); x.arc(px, py, rr, 0, Math.PI * 2); x.fill();
  }
  return toTexture(c);
}

// Soft cloud alpha sheet (transparent PNG-style) for a drifting sky layer.
export function makeSoftClouds() {
  const S = 512;
  const c = document.createElement('canvas'); c.width = c.height = S;
  const x = c.getContext('2d');
  x.clearRect(0, 0, S, S);
  for (let i = 0; i < 26; i++) {
    const px = Math.random() * S, py = Math.random() * S, rr = 30 + Math.random() * 90;
    const g = x.createRadialGradient(px, py, 0, px, py, rr);
    const a = 0.12 + Math.random() * 0.22;
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = g; x.beginPath(); x.arc(px, py, rr, 0, Math.PI * 2); x.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
